import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import Joi from 'joi';

import { distributorSchemas, userSchemas } from 'schemas';
import {
    useCreateNetworkMutation,
    useGetDistributorCompaniesQuery,
    useGetInvestmentFundsQuery,
    useGetNetworkQuery,
    useGetRolesQuery,
    useGetUsersQuery,
    useUpdateNetworkMutation,
} from 'api/main';
import { InternalErrors, ORDER_BY, RoleNames } from 'utils/constants';
import { parseFullName, sortStringValueByKey, validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CheckboxLabel,
    CustomTextField,
    ErrorMessage,
    Loader,
    TransferList,
} from 'components';
import { Box, FormGroup, Typography } from '@mui/material';

const schema = {
    name: Joi.string().required().messages({ 'string.empty': texts.EMPTY_NETWORK_NAME }),
    partners: Joi.array().min(1).messages({ 'array.min': texts.EMPTY_PARTNER_LIST }),
    products: Joi.array().min(1).messages({ 'array.min': texts.EMPTY_PRODUCT_LIST }),
    admins: Joi.array().min(1).messages({ 'array.min': texts.EMPTY_ADMIN_LIST }),
};

const NetworkForm = () => {
    const dispatch = useDispatch();
    const { id: networkId } = useParams();

    const [editingNetwork, setEditingNetwork] = useState({
        investmentFunds: [],
        distributorCompanies: [],
        networkAdmins: [],
    });
    const [form, setForm] = useState({ name: '', partners: [], products: [], admins: [] });
    const [errors, setErrors] = useState({ name: '', partners: '', products: '', admins: '' });
    const [nameExists, setNameExists] = useState(false);
    const [mismatchError, setMismatchError] = useState(false);

    const { data: network = {}, isLoading: dataLoading } = useGetNetworkQuery(networkId, {
        skip: !networkId,
    });
    const {
        data: { distributors = [] } = { distributors: [] },
        isLoading: distributorsLoader,
        isFetching: distributorsFetching,
    } = useGetDistributorCompaniesQuery(
        distributorSchemas.getDistributorList({
            pagination: false,
            orderBy: ORDER_BY.distributorCompanyName,
            conventionSignedWithFund: form.products.length ? form.products : null,
        }),
        { skip: network?.investmentFunds?.length <= 0 && networkId }
    );
    const { data: roles = [], isLoading: rolesLoader } = useGetRolesQuery();
    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery();
    const { data: { userList: networkAdmins = [] } = { userList: [] }, isLoading: usersLoader } =
        useGetUsersQuery(
            userSchemas.getUsers({
                pagination: false,
                role: [roles.find(r => r.name === RoleNames.NETWORK_ADMIN)?.id],
                networkAdmin: [0],
            }),
            { skip: !roles.length }
        );
    const [
        createNetwork,
        {
            isLoading: createLoader,
            isSuccess: createSuccess,
            isError: createError,
            error: creationError,
        },
    ] = useCreateNetworkMutation();
    const [
        updateNetwork,
        {
            isLoading: updateLoader,
            isSuccess: updateSuccess,
            isError: updateError,
            error: updatingError,
        },
    ] = useUpdateNetworkMutation();

    const filteredProducts = useMemo(
        () =>
            fundList.filter(
                p =>
                    (p.enabled || editingNetwork?.investmentFunds?.find(f => f.id === p.id)) &&
                    p.visible
            ),
        [fundList, editingNetwork]
    );
    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setForm(prevState => ({ ...prevState, [key]: value }));
            setErrors(prevState => ({ ...prevState, [key]: '' }));
            setNameExists(false);
        },
        []
    );
    const handleChangeProducts = useCallback(e => {
        const { checked, value } = e.target;
        setForm(prevState => {
            if (checked) return { ...prevState, products: [...prevState.products, +value] };
            return { ...prevState, products: prevState.products.filter(p => p !== +value) };
        });
        setErrors(prevState => ({ ...prevState, products: '' }));
        setNameExists(false);
    }, []);
    const handleChangeList = useCallback(
        key => value => {
            setForm(prevState => ({ ...prevState, [key]: value }));
            setErrors(prevState => ({ ...prevState, [key]: '' }));
        },
        []
    );

    const goBack = useCallback(() => {
        dispatch(push('/networks'));
    }, [dispatch]);

    const handleSubmit = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setMismatchError(false);
            setErrors({});
            if (networkId) {
                updateNetwork({ ...form, id: networkId });
            } else {
                createNetwork(form);
            }
        } else {
            setErrors(newErrors);
        }
    }, [form, networkId, createNetwork, updateNetwork]);

    useEffect(() => {
        if (networkId && network?.id) {
            setEditingNetwork(network);
        }
    }, [networkId, network]);

    useEffect(() => {
        if (createSuccess || updateSuccess) {
            goBack();
        }
    }, [goBack, createSuccess, updateSuccess]);

    useEffect(() => {
        if (createError && creationError?.data?.detail === InternalErrors.NETWORK_NAME_EXISTS) {
            setNameExists(true);
        } else if (creationError?.data?.detail === InternalErrors.NETWORK_ASSIGN_MISMATCH) {
            setMismatchError(true);
        }
    }, [goBack, createError, creationError]);

    useEffect(() => {
        if (updateError && updatingError?.data?.detail === InternalErrors.NETWORK_NAME_EXISTS) {
            setNameExists(true);
        } else if (updatingError?.data?.detail === InternalErrors.NETWORK_ASSIGN_MISMATCH) {
            setMismatchError(true);
        }
    }, [goBack, updateError, updatingError]);

    // updates the form when all data is IN (aka all GET requests are done)
    // for editing a network
    useEffect(() => {
        if (editingNetwork?.id) {
            setForm({
                id: editingNetwork.id,
                name: editingNetwork.name,
                products: editingNetwork.investmentFunds.map(p => p.id),
                admins: [...editingNetwork.networkAdmins],
                partners: [...editingNetwork.distributorCompanies],
            });
        }
    }, [editingNetwork]);

    return (
        <Box width="100%" padding={3}>
            <Typography variant="h5" align="left" gutterBottom>
                {networkId ? texts.EDIT_NETWORK : texts.CREATE_NETWORK}
            </Typography>
            <Loader
                loading={
                    fundsLoader || rolesLoader || usersLoader || distributorsLoader || dataLoading
                }
                marginTop={20}
            >
                <Box padding={2}>
                    <CustomTextField
                        fullWidth
                        label={texts.NETWORK_NAME}
                        value={form.name}
                        onChange={handleChange('name')}
                        error={nameExists ? texts.NETWORK_NAME_EXISTS : errors.name}
                    />
                    <FormGroup>
                        <Typography gutterBottom>{texts.PRODUITS}</Typography>
                        <Box pl={3}>
                            {filteredProducts.map(p => (
                                <Box key={p.id}>
                                    <CheckboxLabel
                                        onChange={handleChangeProducts}
                                        value={p.id}
                                        checked={!!form.products.find(f => f === p.id)}
                                        label={p.name}
                                    />
                                </Box>
                            ))}
                        </Box>
                        <ErrorMessage message={errors.products} />
                    </FormGroup>
                    <TransferList
                        label={texts.PARTNERS}
                        sorterFunction={sortStringValueByKey('corporateName')}
                        renderText={d => d.corporateName}
                        items={[
                            ...distributors.map(d => d[0]),
                            ...editingNetwork.distributorCompanies,
                        ]}
                        value={form.partners}
                        onChange={handleChangeList('partners')}
                        error={errors.partners}
                        loading={distributorsFetching}
                    />
                    <TransferList
                        label={texts.ANIMATORS}
                        sorterFunction={sortStringValueByKey('firstName')}
                        renderText={admin => parseFullName(admin)}
                        items={[...networkAdmins, ...editingNetwork.networkAdmins]}
                        value={form.admins}
                        onChange={handleChangeList('admins')}
                        error={errors.admins}
                    />
                    <ErrorMessage
                        message={mismatchError ? texts.NETWORK_PRODUCT_MISMATCH : ''}
                        align="center"
                    />
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
                    <CancelButton onClick={goBack}>{texts.CANCEL}</CancelButton>
                    <ActionButton
                        loading={createLoader || updateLoader}
                        disabled={createLoader || updateLoader}
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        <Loader loading={createLoader || updateLoader} marginTop={0}>
                            {texts.SAVE}
                        </Loader>
                    </ActionButton>
                </Box>
            </Loader>
        </Box>
    );
};

export default NetworkForm;
